import { SelectItem } from '@/types/ui';
import Badge from '../../Badge/Badge';
import { CheckState } from '../Checkbox/Checkbox';
import type { NestedKeySort } from './TreeSelect.types';

export const collapseSingleChildLeaves = (filterOptions: SelectItem[]): SelectItem[] => {
  const groups = filterOptions.reduce<Record<string, SelectItem[]>>((acc, filterOption) => {
    const parts = filterOption.value.split('__');
    const parentPath = parts.slice(0, -1).join('__');
    if (!acc[parentPath]) acc[parentPath] = [];
    acc[parentPath].push(filterOption);
    return acc;
  }, {});

  return filterOptions.map(filterOption => {
    const parts = filterOption.value.split('__');
    const parentPath = parts.slice(0, -1).join('__');
    const siblings = groups[parentPath] || [];

    if (siblings.length === 1 && parentPath) {
      return {
        label: parentPath,
        value: parentPath,
      };
    }

    return filterOption;
  });
};

export type NestedOptions = Record<string, { path: string; checked: CheckState; subOptions: NestedOptions }>;

export type NestedTreeNode = NestedOptions[string];

export function sortNestedOptionEntries(
  nested: NestedOptions,
  compare: (a: string, b: string) => number,
): [string, NestedTreeNode][] {
  return Object.entries(nested).sort(([a], [b]) => compare(a, b));
}

/** Sorts sibling keys at every depth once. Preserves node data; use for render instead of sorting in each `renderNestedOptions` pass. */
export function sortNestedOptionsTree(nested: NestedOptions, compare: (a: string, b: string) => number): NestedOptions {
  const sortedEntries = sortNestedOptionEntries(nested, compare);
  const result: NestedOptions = {};
  for (const [key, node] of sortedEntries) {
    result[key] = {
      path: node.path,
      checked: node.checked,
      subOptions: sortNestedOptionsTree(node.subOptions, compare),
    };
  }
  return result;
}

function isDigitChar(c: string): boolean {
  return c >= '0' && c <= '9';
}

/** Plain calendar year as object key, e.g. "2025". */
function isYearOnlyKey(key: string): boolean {
  if (key.length !== 4) return false;
  for (let i = 0; i < 4; i++) {
    if (!isDigitChar(key[i])) return false;
  }
  return true;
}

/**
 * Parses "Qx YYYY" sibling keys (e.g. "Q3 2025"). No regex — avoids allocations in sort comparators.
 * Returns null if the shape does not match.
 */
function parseQuarterYearKey(key: string): { year: number; quarter: number } | null {
  if (key.length < 7 || key[0] !== 'Q') return null;
  let i = 1;
  while (i < key.length && isDigitChar(key[i])) i += 1;
  if (i === 1) return null;
  const quarter = Number(key.slice(1, i));
  if (i >= key.length || key[i] !== ' ') return null;
  let j = i + 1;
  while (j < key.length && key[j] === ' ') j += 1;
  const yearPart = key.slice(j);
  if (yearPart.length !== 4) return null;
  let year = 0;
  for (let k = 0; k < 4; k++) {
    if (!isDigitChar(yearPart[k])) return null;
    year = year * 10 + (yearPart.charCodeAt(k) - 48);
  }
  return { year, quarter };
}

const quarterYearParseCache = new Map<string, { year: number; quarter: number } | null>();

function parseQuarterYearKeyCached(key: string): { year: number; quarter: number } | null {
  const cached = quarterYearParseCache.get(key);
  if (cached !== undefined) return cached;
  const result = parseQuarterYearKey(key);
  quarterYearParseCache.set(key, result);
  return result;
}

export function compareNestedKeysForSort(mode: NestedKeySort, a: string, b: string): number {
  if (mode === 'chronological-desc') {
    if (isYearOnlyKey(a) && isYearOnlyKey(b)) {
      return Number(b) - Number(a);
    }
    const qa = parseQuarterYearKeyCached(a);
    const qb = parseQuarterYearKeyCached(b);
    if (qa !== null && qb !== null) {
      if (qa.year !== qb.year) {
        return qb.year - qa.year;
      }
      return qa.quarter - qb.quarter;
    }
  }
  return a.localeCompare(b);
}

/**
 * Counts the number of selected nodes (self + descendants) for indication mode.
 */
export const getSelectedCountInSubtree = (
  properties: { path: string; checked: CheckState; subOptions: NestedOptions },
  selectedSet: Set<string>,
  isIndicationMode: boolean,
): number => {
  if (!isIndicationMode) return 0;

  let count = selectedSet.has(properties.path) ? 1 : 0;

  const countInSubOptions = (subOptions: NestedOptions) => {
    Object.values(subOptions).forEach(subProperty => {
      if (selectedSet.has(subProperty.path)) {
        count += 1;
      }
      if (Object.keys(subProperty.subOptions).length > 0) {
        countInSubOptions(subProperty.subOptions);
      }
    });
  };

  countInSubOptions(properties.subOptions);
  return count;
};

/**
 * Builds a map where each key is a node's path and the value is an array of all its descendant paths.
 * This is used to efficiently find and select/deselect all descendants of a node.
 *
 * Single-pass O(N × depth): for each option, derive its ancestor paths from the
 * `__`-delimited segments and register the option as a descendant of each ancestor.
 */
export const buildDescendantMap = (
  fixedOptions: SelectItem[],
  selectionMode: 'standard' | 'indication',
): Map<string, string[]> => {
  const map = new Map<string, string[]>();
  if (selectionMode === 'standard') return map;

  for (const option of fixedOptions) {
    const optionPath = option.value;

    // Ensure every option has an entry (leaves get an empty array)
    if (!map.has(optionPath)) map.set(optionPath, []);

    // Walk ancestor paths and register this option as a descendant of each
    const parts = optionPath.split('__');
    let ancestorPath = '';
    for (let i = 0; i < parts.length - 1; i++) {
      ancestorPath = i === 0 ? parts[i] : `${ancestorPath}__${parts[i]}`;
      let descendants = map.get(ancestorPath);
      if (!descendants) {
        descendants = [];
        map.set(ancestorPath, descendants);
      }
      descendants.push(optionPath);
    }
  }

  return map;
};

/**
 * Walks the nested options tree and returns the highest-level checked paths.
 * If all children of a branch are checked, only the branch path is returned
 * (not each child individually).
 */
export function getAllCheckedPaths(nestedObject: NestedOptions): string[] {
  const getCheckedPaths = (currentNestedOption: NestedOptions): { allSubOptionsChecked: boolean; paths: string[] } => {
    let allChecked = true;
    const paths: string[] = [];
    Object.values(currentNestedOption).forEach(({ path, checked, subOptions }) => {
      const hasChildren = Object.keys(subOptions).length > 0;
      if (hasChildren) {
        const { allSubOptionsChecked, paths: childPaths } = getCheckedPaths(subOptions);
        if (allSubOptionsChecked) paths.push(path);
        else {
          paths.push(...childPaths);
          allChecked = false;
        }
      } else {
        if (checked === true) paths.push(path);
        else allChecked = false;
      }
    });
    return { allSubOptionsChecked: allChecked, paths };
  };
  return getCheckedPaths(nestedObject).paths;
}

export const getBadgeForIndicationMode = ({
  isIndication,
  selectedSet,
  properties,
  hasSubOptions,
  isCurrentBranchOpen,
  selectedCount,
}: {
  isIndication: boolean;
  selectedSet: Set<string>;
  properties: { path: string; checked: CheckState; subOptions: NestedOptions };
  hasSubOptions: boolean;
  isCurrentBranchOpen: boolean;
  selectedCount: number;
}): React.ReactNode => {
  if (!isIndication) return null;
  if (selectedSet.size === 0) return null;

  const isSelected = selectedSet.has(properties.path);

  const isCollapsedWithSelections = hasSubOptions && !isCurrentBranchOpen && selectedCount > 0;

  const isExpandedSelected = hasSubOptions && isCurrentBranchOpen && isSelected;

  const isLeafSelected = !hasSubOptions && isSelected;

  if (!isCollapsedWithSelections && !isExpandedSelected && !isLeafSelected) return null;

  const label = isCollapsedWithSelections ? `${selectedCount}` : '1';

  return (
    <Badge size="S" variant="light" color="blue" data-testid="badgeCount">
      {label}
    </Badge>
  );
};

/** Searches the nested options tree for paths matching the search input. Returns a Set of matching paths and their ancestors. */
export function searchNestedOptionsTree(nestedOptions: NestedOptions, searchInput: string): Set<string> {
  if (!searchInput) return new Set<string>();
  const opened = new Set<string>();
  const lowerSearch = searchInput.toLowerCase();
  const ancestors: string[] = [];
  const searchInTree = (currentNestedOptions: NestedOptions) => {
    Object.values(currentNestedOptions).forEach(({ path, subOptions }) => {
      const pathSegments = path.split('__');
      const matches = pathSegments.some(segment => segment.toLowerCase().includes(lowerSearch));
      if (matches) {
        for (const ancestor of ancestors) opened.add(ancestor);
        opened.add(path);
      }
      if (Object.keys(subOptions).length > 0) {
        ancestors.push(path);
        searchInTree(subOptions);
        ancestors.pop();
      }
    });
  };
  searchInTree(nestedOptions);
  return opened;
}

/** State updater that toggles an item in a Set. Use with `useState<Set<string>>` setters. */
export function toggleSetItem(set: Set<string>, item: string): Set<string> {
  const next = new Set(set);
  if (next.has(item)) next.delete(item);
  else next.add(item);
  return next;
}

/** Extracts the last segment (label) from each `__`-delimited path. */
export function extractValueLabels(value: string[]): string[] {
  if (!value || value.length === 0) return [];
  return value.map(valueItem => {
    const lastSep = valueItem.lastIndexOf('__');
    return lastSep === -1 ? valueItem : valueItem.slice(lastSep + 2);
  });
}

/** Returns the set of all ancestor paths for the given `__`-delimited path. */
export function getAncestorPaths(path: string): string[] {
  const parts = path.split('__');
  const ancestors: string[] = [];
  let current = '';
  for (let i = 0; i < parts.length - 1; i++) {
    current = i === 0 ? parts[i] : `${current}__${parts[i]}`;
    ancestors.push(current);
  }
  return ancestors;
}
